<template>
  <v-form v-model="valid" ref="form" lazy-validation>
    <v-card flat>
      <v-card-title>
        <v-icon>mdi-home</v-icon>
        {{ titre }}
        <v-btn
          small
          v-if="
            propositionAdresse && adressePropose != null && !showAdressePropose
          "
          outlined
          color="secondary"
          v-on:click="showAdressePropose = !showAdressePropose"
          >afficher {{ titreProposition }}</v-btn
        >
        <v-btn
          small
          v-if="
            propositionAdresse && adressePropose != null && showAdressePropose
          "
          outlined
          color="secondary"
          v-on:click="showAdressePropose = !showAdressePropose"
          >cacher {{ titreProposition }}</v-btn
        >
      </v-card-title>
      <v-card-text>
        <v-row dense v-if="showAdressePropose">
          <v-textarea solo :value="adressePropose"></v-textarea>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              autofocus
              dense
              filled
              v-model="libelle"
              :rules="[obligatoireRule(), serveurRule(`${itemPath}.libelle`)]"
              label="Libellé"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              dense
              filled
              v-model="ligne1"
              :rules="[obligatoireRule(), serveurRule(`${itemPath}.ligne1`)]"
              label="Ligne 1"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              dense
              filled
              v-model="ligne2"
              label="Ligne 2"
              :rules="[serveurRule(`${itemPath}.ligne2`)]"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              dense
              filled
              v-model="ligne3"
              label="Ligne 3"
              :rules="[serveurRule(`${itemPath}.ligne3`)]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              dense
              filled
              v-model="codePostal"
              :rules="[
                obligatoireRule(),
                serveurRule(`${itemPath}.codePostal`),
              ]"
              label="Code postal"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              dense
              filled
              v-model="cedex"
              :rules="[serveurRule(`${itemPath}.cedex`)]"
              label="Cedex / SP"
            />
          </v-col>

          <v-col cols="6">
            <enumAutocomplete
              v-model="ville"
              type="ville"
              :rules="[
                localiteOuVilleRules(),
                serveurRule(`${itemPath}.ville`),
              ]"
              :parametresRecherche="filtreCodePostal"
              dense
              filled
              clearable
              :disabled="villeDisabled"
              hint="Saisissez une localité en texte libre si la ville n'est pas trouvée"
              label="Ville"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              dense
              filled
              v-model="localite"
              :rules="[
                localiteOuVilleRules(),
                serveurRule(`${itemPath}.localite`),
              ]"
              label="Localité"
              clearable
              :disabled="localiteDisabled"
            />
          </v-col>

          <v-col cols="6">
            <enumAutocomplete
              v-model="departement"
              type="departement"
              :rules="[
                departementRule(),
                serveurRule(`${itemPath}.departement`),
              ]"
              :parametresRecherche="filtreDepartement"
              dense
              filled
              clearable
              label="Département"
            />
          </v-col>

          <v-col cols="6">
            <enumAutocomplete
              v-model="pays"
              type="pays"
              dense
              filled
              label="Pays"
              clearable
              :parametresRecherche="filtrePays"
              :rules="[paysRule(), serveurRule(`${itemPath}.pays`)]"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn text color="primary" @click="valider()">Ok</v-btn>
        <v-btn text @click="annuler()">Annuler</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { copieAdresse } from './adresseUtil.js';
import ServeurRule from '@/plugins/mixins/ServeurRule';
import ValidationRules from '@/plugins/mixins/ValidationRules';

export default {
  mixins: [ServeurRule, ValidationRules],
  inject: ['errors'],
  props: {
    value: {
      type: Object,
    },
    titre: {
      type: String,
    },
    itemPath: {
      type: String,
      default: '',
    },
    propositionAdresse: {
      type: Boolean,
      default: false,
    },
    adressePropose: {
      type: String,
      default: null,
    },
    titreProposition: {
      type: String,
      default: 'adresse proposée',
    },
  },
  data() {
    const data = copieAdresse({}, this.value);
    data.showAdressePropose = false;
    data.valid = true;
    return data;
  },
  watch: {
    value(newValue) {
      copieAdresse(this, newValue);
      this.$refs.form.resetValidation();
    },
    ville(newValue) {
      if (newValue.codePostal && !this.codePostal) {
        this.codePostal = newValue.codePostal;
      }
    },
  },
  computed: {
    filtreCodePostal() {
      if (this.codePostal) {
        return {
          codePostal: this.codePostal,
        };
      } else {
        return null;
      }
    },
    filtreDepartement() {
      if (this.pays && this.pays.libelle !== 'FRANCE') {
        return {
          code: '00', //HORS FRANCE
        };
      } else if (this.codePostal && this.codePostal.length >= 2) {
        return {
          code: this.codePostal.substring(0, 2),
        };
      } else {
        return null;
      }
    },
    filtrePays() {
      if (this.ville) {
        return {
          code: 'CODE000083', //FRANCE
        };
      } else {
        return null;
      }
    },
    localiteOuVille() {
      return !!(this.localite || this.ville);
    },
    villeDisabled() {
      if (this.localiteOuVille) {
        return !!this.localite;
      } else {
        return false;
      }
    },
    localiteDisabled() {
      if (this.localiteOuVille) {
        return !!this.ville;
      } else {
        return false;
      }
    },
  },
  methods: {
    localiteOuVilleRules() {
      return () =>
        this.localiteOuVille || 'La localité ou la ville est obligatoire';
    },
    departementRule() {
      return () => !!this.departement || 'Le département est obligatoire';
    },
    paysRule() {
      return () => !!this.pays || 'Le pays est obligatoire';
    },
    valider() {
      this.validationServeur = false;
      const validate = this.$refs.form.validate();
      this.validationServeur = true;

      if (validate) {
        const adresse = copieAdresse({}, this);
        this.$emit('input', adresse);
        this.$emit('change');
      }
    },

    annuler() {
      this.$emit('annuler');
    },
  },
};
</script>
